/* eslint-disable no-param-reassign */
export type ValidateOptions = {
  minMax: any;
  numericFilter: boolean;
  isFloat: boolean;
  maxLength:number;
  minMaxCallBack?: () => void
};

// type="number" でも打ててしまう、整数の mm に要らない文字
const NON_INTEGER_KEYS: readonly string[] = ['-', '+', 'e', 'E', '.'];
const NON_DIGIT_PATTERN: RegExp = /[^0-9]/g;
const FULL_WIDTH_DIGIT_PATTERN: RegExp = /[０-９]/g;
// 半角・全角の数字以外の文字
const NON_DIGIT_INPUT_PATTERN: RegExp = /[^0-9０-９]/;
// 小数点（全角を含む）から後ろ
const DECIMAL_PART_PATTERN: RegExp = /[.．].*$/;
// 全角数字と半角数字の文字コードの差
const FULL_WIDTH_DIGIT_OFFSET: number = 0xFEE0;
const INSERT_TEXT_INPUT_TYPE: string = 'insertText';
const CLIPBOARD_TEXT_FORMAT: string = 'text';
const CHANGE_EVENT: string = 'change';

// 入力最小最大検証と数値フィルター
export default class InputValidationUtil {
  /**
     * 要素の検証設定機能
     * @param element
     * @param minMax
     * @param numericFilter
     * @param isFloat
     */
  static setValidation(element: HTMLInputElement, options:ValidateOptions) { // minMax: any = null, numericFilter: boolean = false, isFloat: boolean = false) {
    if (options.numericFilter) {
      this.setInputFilter([element], (value: any) => /^-?\d*[.,]?\d*$/.test(value));
    }
    if (options.minMax != null) {
      element.setAttribute('min', options.minMax.min);
      element.setAttribute('max', options.minMax.max);
      element.addEventListener('focusout', () => this.validateMinMax(element, options, options.isFloat));
    }

    if (options.maxLength > 0) {
      element.addEventListener('keydown', () => this.limit(element, options.maxLength));
    }
  }

  /**
     * 最小最大検証
     * @param element
     * @param minMax
     * @param isFloat
     */
  static validateMinMax(element: HTMLInputElement, options: ValidateOptions, isFloat: boolean = false) {
    const validate = (_value: number) => {
      element.value = _value.toString();
      if (options?.minMaxCallBack) options.minMaxCallBack();
    }

    const minMax = options.minMax;
    const value = isFloat ? parseFloat((element.value)) : Math.floor(Number(element.value));
    if (Number.isNaN(value)) {
      validate(0);
    }
    if (!isFloat) {
      validate(value);
    }
    if (minMax) {
      if (value > minMax.max) {
        validate(minMax.max);
      }
      if (value < minMax.min) {
        validate(minMax.min);
      }
    }
  }

  static limit(element: HTMLInputElement, _maxChars:number) {
    if (element.value.length > _maxChars) {
      element.value = element.value.substring(0, _maxChars);
    }
  }

  /**
   * 数値入力フィルター
   * @param textbox
   * @param inputFilter
   */
  static setInputFilter(textbox: any, inputFilter: any) {
    ['input', 'keydown', 'keyup', 'mousedown', 'mouseup', 'select', 'contextmenu', 'drop'].forEach((event: any) => {
      for (let i = 0; i < textbox.length; i += 1) {
        textbox[i].addEventListener(event, function () {
          if (inputFilter(this.value)) {
            this.oldValue = this.value;
            this.oldSelectionStart = this.selectionStart;
            this.oldSelectionEnd = this.selectionEnd;
          } else if (this.hasOwnProperty('oldValue')) {
            this.value = this.oldValue;
            this.setSelectionRange(this.oldSelectionStart, this.oldSelectionEnd);
          }
        });
      }
    });
  }

  /**
   * 小数点フィルター
     * @param element
     * @param digits
   */
  static setDecimalPoint(element: HTMLInputElement, digits: number) {
    element.addEventListener('focusout', () => {
      // 小数点第四位で四捨五入
      element.value = Number(Number(element.value).toFixed(digits)).toString();
    });
  }

  /**
   * 整数の mm だけを入力できるようにする。キー入力の「- + e .」を止め、貼り付け・ドロップは数字だけにする
   * 下限・上限の置き換えは各画面の change 処理に任せる
   */
  public static setIntegerMmFilter(element: HTMLInputElement): void {
    element.addEventListener('keydown', (e: KeyboardEvent) => {
      // Ctrl/Cmd との組み合わせ（コピー・貼り付けなど）は通す
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (NON_INTEGER_KEYS.includes(e.key)) e.preventDefault();
    });
    // ソフトウェアキーボード（iPad など）は keydown で文字が分からないことがあるため、入る文字でも止める
    element.addEventListener('beforeinput', (e: InputEvent) => {
      if (e.inputType !== INSERT_TEXT_INPUT_TYPE || !e.data) return;
      // 全角数字はブラウザが半角に直して入れるので通す
      if (NON_DIGIT_INPUT_PATTERN.test(e.data)) e.preventDefault();
    });
    element.addEventListener('paste', (e: ClipboardEvent) => InputValidationUtil.insertDigitsOnly(element, e, e.clipboardData));
    element.addEventListener('drop', (e: DragEvent) => InputValidationUtil.insertDigitsOnly(element, e, e.dataTransfer));
  }

  /**
   * 全角数字を半角にし、数字以外を取り除く
   */
  private static toDigits(text: string): string {
    return text
      .replace(FULL_WIDTH_DIGIT_PATTERN, (c: string) => String.fromCharCode(c.charCodeAt(0) - FULL_WIDTH_DIGIT_OFFSET))
      .replace(NON_DIGIT_PATTERN, '');
  }

  /**
   * 貼り付け・ドロップする文字に数字以外があれば、数字だけにして入れ直す
   */
  private static insertDigitsOnly(element: HTMLInputElement, e: Event, data: DataTransfer | null): void {
    if (!data) return;
    const text: string = data.getData(CLIPBOARD_TEXT_FORMAT);
    // 小数点の後ろまで数字として残すと桁が変わる（1500.5 → 15005）ため、整数部だけを使う
    const digits: string = InputValidationUtil.toDigits(text.replace(DECIMAL_PART_PATTERN, ''));
    if (digits === text) return;
    e.preventDefault();
    if (!digits) return;
    // type="number" は選択位置を扱えないため、欄の値ごと置き換えて change で各画面の処理を通す
    element.value = digits;
    element.dispatchEvent(new Event(CHANGE_EVENT, { bubbles: true }));
  }
}
